import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { ACTIVITY_CITATION_TYPE } from '@trustero/trustero-api-web/lib/activity/activity_pb'
import { ATTACHMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  ExamineAndTestAbsoluteRoutes,
  SchedulingAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import { Filter, Transform } from 'src/Utils/filter/filter'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'

export type NormalizedLogEntry = {
  id: string
  createdat: number
  actor: string
  actortype: number
  message: string
  subjectmodeltype?: number
  subjectmodelid?: string
  attachmentid?: string
  attachmenttype?: number
  isLegacy: boolean
  isError: boolean
  citations: Array<{ text: string; url: string; citationType?: number }>
  rawCitations?: Array<{ type: number; text: string; citationId?: string }>
  infos: Array<{ label: string; value: string }>
  errors: Array<{ label: string; value: string }>
  hasInfo?: boolean
  hasError?: boolean
  infoCount?: number
  errorCount?: number
}

export const CITATION_TYPE_NAMES: Record<number, string> = {
  [ACTIVITY_CITATION_TYPE.AUDIT]: 'Audit',
  [ACTIVITY_CITATION_TYPE.FRAMEWORK]: 'Framework',
  [ACTIVITY_CITATION_TYPE.POLICY]: 'Policy',
  [ACTIVITY_CITATION_TYPE.CONTROL]: 'Control',
  [ACTIVITY_CITATION_TYPE.EVIDENCE]: 'Evidence',
  [ACTIVITY_CITATION_TYPE.REQUEST]: 'Request',
  [ACTIVITY_CITATION_TYPE.RISK]: 'Risk',
  [ACTIVITY_CITATION_TYPE.VENDOR]: 'Vendor',
  [ACTIVITY_CITATION_TYPE.CHAT]: 'Chat',
  [ACTIVITY_CITATION_TYPE.TI_PLAYBOOK]: 'TI Playbook',
  [ACTIVITY_CITATION_TYPE.RECEPTOR_OBJ]: 'Receptor',
  [ACTIVITY_CITATION_TYPE.SCHEDULE]: 'Schedule',
  [ACTIVITY_CITATION_TYPE.QUESTIONNAIRE]: 'Questionnaire',
  [ACTIVITY_CITATION_TYPE.USER_OBJ]: 'User',
  [ACTIVITY_CITATION_TYPE.SERVICE]: 'Service',
  [ACTIVITY_CITATION_TYPE.KNOWLEDGE_BASE]: 'Knowledge Base',
  [ACTIVITY_CITATION_TYPE.CHAT_CONTEXT]: 'Chat Context',
  [ACTIVITY_CITATION_TYPE.AUDIT_RUN]: 'Control Test',
}

export const MAIN_CITATION_TYPES = [
  ACTIVITY_CITATION_TYPE.POLICY,
  ACTIVITY_CITATION_TYPE.CONTROL,
  ACTIVITY_CITATION_TYPE.EVIDENCE,
]

export const activityFilters: Filter[] = [
  {
    key: FilterParam.ACTIVITY_ACTOR,
    property: 'actor',
  },
  {
    key: FilterParam.CITATION_POLICY,
    property: 'citation_policy',
  },
  {
    key: FilterParam.CITATION_CONTROL,
    property: 'citation_control',
  },
  {
    key: 'citation_control_id',
    property: 'citation_control_id',
  },
  {
    key: FilterParam.CITATION_EVIDENCE,
    property: 'citation_evidence',
  },
  {
    key: FilterParam.CITATION_TYPES,
    property: 'citation_types',
  },
  {
    key: 'subject_model_id',
    property: 'subject_model_id',
  },
  {
    key: 'subject_id',
    property: 'subject_id',
  },
]

export const transformActivity: Transform<NormalizedLogEntry> = (log) => {
  const typeName = log.subjectmodeltype
    ? MODEL_TYPE_NAMES[log.subjectmodeltype] || `Type ${log.subjectmodeltype}`
    : 'Unknown'

  const getCitationsForType = (targetType: number) =>
    (log.rawCitations || [])
      .filter((c) => c.type === targetType)
      .map((c) => c.text)

  // Collect all associated entity IDs for exact subject / attachment / citation URL / citationId matching
  const allEntityIds: string[] = []
  if (log.subjectmodelid) allEntityIds.push(log.subjectmodelid)
  if (log.attachmentid) allEntityIds.push(log.attachmentid)
  const rawCites = log.rawCitations || []
  rawCites.forEach((c) => {
    if (c.citationId) allEntityIds.push(c.citationId)
  })
  const logCites = log.citations || []
  logCites.forEach((c) => {
    if (c.url) {
      const match = c.url.match(
        /\/(?:controls|policies|evidence|requests|risks|receptors|vendors|services|schedules|questionnaire|frameworks)\/show\/([^?#]+)/,
      )
      if (match && match[1]) {
        allEntityIds.push(decodeURIComponent(match[1]))
      }
    }
  })

  // Policies (match by description/text or ID/url)
  const policyCitations = (log.rawCitations || []).filter(
    (c) => c.type === ACTIVITY_CITATION_TYPE.POLICY,
  )
  const policyTexts = policyCitations.map((c) => c.text)
  const policyValues: string[] = [...policyTexts]
  policyCitations.forEach((c) => {
    if (c.citationId) policyValues.push(c.citationId)
  })
  if (log.subjectmodeltype === MODEL_TYPE.POLICY && log.subjectmodelid) {
    policyValues.push(log.subjectmodelid)
  }
  if (log.attachmenttype === ATTACHMENT_TYPE.POLICY && log.attachmentid) {
    policyValues.push(log.attachmentid)
  }
  const policyCitationsFromLog = log.citations || []
  policyCitationsFromLog.forEach((c) => {
    if (c.citationType === ACTIVITY_CITATION_TYPE.POLICY && c.url) {
      const match = c.url.match(/\/policies\/show\/([^?#]+)/)
      if (match && match[1]) {
        policyValues.push(decodeURIComponent(match[1]))
      }
    }
  })

  // Controls (match by description/text or ID/url)
  const controlCitations = (log.rawCitations || []).filter(
    (c) => c.type === ACTIVITY_CITATION_TYPE.CONTROL,
  )
  const controlTexts = controlCitations.map((c) => c.text)
  const controlIds: string[] = []
  controlCitations.forEach((c) => {
    if (c.citationId) controlIds.push(c.citationId)
  })
  if (log.subjectmodeltype === MODEL_TYPE.CONTROL && log.subjectmodelid) {
    controlIds.push(log.subjectmodelid)
  }
  if (log.attachmenttype === ATTACHMENT_TYPE.CONTROL && log.attachmentid) {
    controlIds.push(log.attachmentid)
  }
  const controlCitationsFromLog = log.citations || []
  controlCitationsFromLog.forEach((c) => {
    if (c.citationType === ACTIVITY_CITATION_TYPE.CONTROL && c.url) {
      const match = c.url.match(/\/controls\/show\/([^?#]+)/)
      if (match && match[1]) {
        controlIds.push(decodeURIComponent(match[1]))
      }
    }
  })

  const otherCitations: string[] = []
  if ((log.rawCitations || []).length === 0) {
    otherCitations.push('No Citation')
  } else {
    const rawCitations = log.rawCitations || []
    rawCitations
      .filter((c) => !MAIN_CITATION_TYPES.includes(c.type))
      .forEach((c) => {
        const typeLabel = CITATION_TYPE_NAMES[c.type] || `Type ${c.type}`
        otherCitations.push(typeLabel)
        if (c.text) {
          otherCitations.push(`${typeLabel}: ${c.text}`)
        }
      })
  }

  const uniqueEntityIds = Array.from(new Set(allEntityIds))

  return {
    actor: log.actor || 'System',
    subjectmodeltype: typeName,
    citation_policy: Array.from(new Set(policyValues)),
    citation_control: controlTexts,
    citation_control_id: Array.from(new Set(controlIds.concat(controlTexts))),
    citation_evidence: getCitationsForType(ACTIVITY_CITATION_TYPE.EVIDENCE),
    citation_request: getCitationsForType(ACTIVITY_CITATION_TYPE.REQUEST),
    citation_risk: getCitationsForType(ACTIVITY_CITATION_TYPE.RISK),
    citation_types: otherCitations,
    subject_model_id: uniqueEntityIds,
    subject_id: uniqueEntityIds,
  }
}

export const getAttachmentType = (citationType: number): number | undefined => {
  if (citationType === ACTIVITY_CITATION_TYPE.CONTROL) {
    return ATTACHMENT_TYPE.CONTROL
  }
  if (citationType === ACTIVITY_CITATION_TYPE.POLICY) {
    return ATTACHMENT_TYPE.POLICY
  }
  return undefined
}

export const getAvatarBg = (ownerName: string): string => {
  if (ownerName.toLowerCase().includes('jira')) return '#0052cc'
  if (ownerName.toLowerCase().includes('trustero')) return '#1f2532'
  const colors = [
    '#0d9488',
    '#2563eb',
    '#7c3aed',
    '#db2777',
    '#ea580c',
    '#16a34a',
  ]
  const charCode = ownerName.charCodeAt(0) || 0
  return colors[charCode % colors.length]
}

export const getAvatarLabel = (ownerName: string): string => {
  if (ownerName.toLowerCase().includes('jira')) return 'J'
  if (ownerName.toLowerCase().includes('trustero')) return 'T'
  return ownerName.charAt(0)
}

export const CITATION_TO_MODEL_TYPE: Record<number, number> = {
  [ACTIVITY_CITATION_TYPE.AUDIT]: MODEL_TYPE.AUDIT,
  [ACTIVITY_CITATION_TYPE.POLICY]: MODEL_TYPE.POLICY,
  [ACTIVITY_CITATION_TYPE.CONTROL]: MODEL_TYPE.CONTROL,
  [ACTIVITY_CITATION_TYPE.EVIDENCE]: MODEL_TYPE.EVIDENCE,
  [ACTIVITY_CITATION_TYPE.REQUEST]: MODEL_TYPE.REQUEST,
  [ACTIVITY_CITATION_TYPE.RISK]: MODEL_TYPE.RISK,
  [ACTIVITY_CITATION_TYPE.VENDOR]: MODEL_TYPE.VENDOR,
  [ACTIVITY_CITATION_TYPE.CHAT]: MODEL_TYPE.CHAT,
  [ACTIVITY_CITATION_TYPE.RECEPTOR_OBJ]: MODEL_TYPE.RECEPTOROBJ,
  [ACTIVITY_CITATION_TYPE.SCHEDULE]: MODEL_TYPE.SCHEDULE,
  [ACTIVITY_CITATION_TYPE.QUESTIONNAIRE]: MODEL_TYPE.QUESTIONNAIRE,
  [ACTIVITY_CITATION_TYPE.SERVICE]: MODEL_TYPE.SERVICE,
  [ACTIVITY_CITATION_TYPE.USER_OBJ]: MODEL_TYPE.USEROBJ,
  [ACTIVITY_CITATION_TYPE.AUDIT_RUN]: MODEL_TYPE.AUDITBOT_RUN,
}

export const getCitationUrl = (
  pageContext: string,
  citationType: number,
  citationId: string,
): string => {
  const ctx = pageContext || ''
  if (citationType === ACTIVITY_CITATION_TYPE.AUDIT_RUN) {
    return ctx
      ? `/${ctx}/${ExamineAndTestAbsoluteRoutes.SHOW}/${citationId}`
      : `/${ExamineAndTestAbsoluteRoutes.SHOW}/${citationId}`
  }
  if (citationType === ACTIVITY_CITATION_TYPE.RECEPTOR_OBJ) {
    return ctx
      ? `/${ctx}/${SchedulingAbsoluteRoutes.INDEX}`
      : `/${SchedulingAbsoluteRoutes.INDEX}`
  }

  const modelType = CITATION_TO_MODEL_TYPE[citationType]
  if (modelType === undefined) return '#'

  const rawLink = generatePermalink({
    pageContext: ctx,
    modelType,
    modelId: citationId,
    isInternalLink: true,
  })
  return rawLink.replace(/^\/{2,}/, '/')
}

export const ACTIVITY_LOG_PAGE_SIZE = 1000
export const ACTIVITY_LOG_MAX_PAGES = 50

export const MODEL_TYPE_NAMES: Record<number, string> = {
  [MODEL_TYPE.CONTROL]: 'Control',
  [MODEL_TYPE.POLICY]: 'Policy',
  [MODEL_TYPE.EVIDENCE]: 'Evidence',
  [MODEL_TYPE.VENDOR]: 'Vendor',
  [MODEL_TYPE.RISK]: 'Risk',
  [MODEL_TYPE.AUDIT]: 'Audit',
  [MODEL_TYPE.AUDITBOT_RUN]: 'Control Test',
  [MODEL_TYPE.RECEPTOROBJ]: 'Receptor',
}
